import { useState, useEffect, useCallback } from 'react'

interface GuestProduct {
  id: string
  name: string
  price: number
  image_urls: string[]
  stock: number
}

interface GuestVariant {
  id: string
  variant_name: string
  variant_value: string
  price: number
  stock_quantity: number
}

export interface GuestCartItem {
  product_id: string
  variant_id: string | null
  quantity: number
  product: GuestProduct
  variant?: GuestVariant
}

const GUEST_CART_KEY = 'buildfast_guest_cart'

export function useGuestCart() {
  const [guestCartItems, setGuestCartItems] = useState<GuestCartItem[]>([])
  const [loading, setLoading] = useState(true)

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(GUEST_CART_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setGuestCartItems(parsed)
        }
      }
    } catch {
      localStorage.removeItem(GUEST_CART_KEY)
    }
    setLoading(false)
  }, [])

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    if (!loading) {
      if (guestCartItems.length > 0) {
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(guestCartItems))
      } else {
        localStorage.removeItem(GUEST_CART_KEY)
      }
    }
  }, [guestCartItems, loading])

  // Calculate cart count
  const guestCartCount = guestCartItems.reduce((sum, item) => sum + item.quantity, 0)

  // Calculate cart total
  const guestCartTotal = guestCartItems.reduce((sum, item) => {
    const price = item.variant?.price ?? item.product?.price ?? 0
    return sum + (price * item.quantity)
  }, 0)

  // Add item to guest cart
  const addToGuestCart = useCallback((
    productId: string,
    variantId: string | null,
    quantity: number,
    productData: GuestProduct,
    variantData?: GuestVariant
  ): { success: boolean; error?: string } => {
    // Check stock
    const availableStock = variantData?.stock_quantity ?? productData.stock

    setGuestCartItems(prev => {
      const existing = prev.find(
        item => item.product_id === productId && item.variant_id === variantId
      )

      if (existing) {
        const newQuantity = existing.quantity + quantity
        if (newQuantity > availableStock) {
          return prev // Don't update if exceeds stock
        }
        return prev.map(item =>
          item.product_id === productId && item.variant_id === variantId
            ? { ...item, quantity: newQuantity }
            : item
        )
      }

      if (quantity > availableStock) {
        return prev // Don't add if exceeds stock
      }

      return [...prev, {
        product_id: productId,
        variant_id: variantId,
        quantity,
        product: productData,
        variant: variantData
      }]
    })

    return { success: true }
  }, [])

  // Update quantity in guest cart
  const updateGuestCartQuantity = useCallback((
    productId: string,
    variantId: string | null,
    quantity: number
  ): { success: boolean; error?: string } => {
    if (quantity < 1) {
      return removeFromGuestCart(productId, variantId)
    }

    setGuestCartItems(prev => {
      const item = prev.find(
        i => i.product_id === productId && i.variant_id === variantId
      )

      if (!item) {
        return prev
      }

      // Check stock
      const availableStock = item.variant?.stock_quantity ?? item.product.stock
      if (quantity > availableStock) {
        return prev // Don't update if exceeds stock
      }

      return prev.map(i =>
        i.product_id === productId && i.variant_id === variantId
          ? { ...i, quantity }
          : i
      )
    })

    return { success: true }
  }, [])

  // Remove item from guest cart
  const removeFromGuestCart = useCallback((
    productId: string,
    variantId: string | null
  ): { success: boolean; error?: string } => {
    setGuestCartItems(prev =>
      prev.filter(item =>
        !(item.product_id === productId && item.variant_id === variantId)
      )
    )
    return { success: true }
  }, [])

  // Clear entire guest cart
  const clearGuestCart = useCallback((): { success: boolean; error?: string } => {
    setGuestCartItems([])
    localStorage.removeItem(GUEST_CART_KEY)
    return { success: true }
  }, [])

  return {
    guestCartItems,
    guestCartCount,
    guestCartTotal,
    loading,
    addToGuestCart,
    updateGuestCartQuantity,
    removeFromGuestCart,
    clearGuestCart
  }
}
